/**
 * Parse API timestamps into Date objects.
 * Backend timestamps are UTC but often arrive without a timezone designator
 * (e.g. "2026-07-15T09:30:00" or "2026-07-15 09:30:00"). Native Date parsing
 * treats those as *local* time, so bare timestamps are normalized to UTC here
 * before parsing. Strings with an explicit offset ("Z", "+05:30") are parsed as-is.
 */
export function parseApiDate(value?: string | number | Date | null): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(value).trim();
  if (!s) return null;
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const isBareDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s);
  const normalized = !hasTimezone && isBareDateTime ? `${s.replace(' ', 'T')}Z` : s;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The kanban task API stores created_at/updated_at in the DB server's local
 * clock (UTC-6) and returns them WITHOUT a timezone designator, while comment
 * stamps in the same payload are true UTC with "Z" (created_at lags a task's
 * first comment by exactly the 6-hour offset). Bare kanban timestamps
 * therefore get this fixed offset appended; strings that already carry
 * Z/offset are parsed as-is, so nothing breaks if the backend moves to UTC
 * with proper designators later.
 */
export function parseKanbanDate(value?: string | number | Date | null): Date | null {
  if (typeof value === 'string') {
    const s = value.trim();
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
    const isBareDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s);
    if (!hasTimezone && isBareDateTime) {
      const envOffset = (process.env.REACT_APP_KANBAN_UTC_OFFSET || '').trim();
      const offset = /^(?:Z|[+-]\d{2}:?\d{2})$/i.test(envOffset) ? envOffset : '-06:00';
      const d = new Date(`${s.replace(' ', 'T')}${offset}`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  return parseApiDate(value);
}

/** Normalize a kanban timestamp to a UTC ISO string ("...Z") for storing in state. */
export function kanbanTsToIso(value?: string | number | Date | null): string | undefined {
  const d = parseKanbanDate(value);
  return d ? d.toISOString() : undefined;
}
